import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';

import UploadImage from '@features/designTool/components/UploadImage';
import { docType } from '@features/assets/utils/AssetsUtil';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { removeSingleFile, uploadMultipleImage, uploadSingleImage } from '@services/assetsService';

import { getShortName, isValidFile } from '@utils/Util';

const UploadFiles = ({
	type,
	itemId,
	onImageIdsChange,
	onMultipleImageDataChange,
	allFilesAndImages,
	error
}) => {
	const [multipleImageData, setMultipleImageData] = useState([]);
	const [uploadedFiles, setUploadedFiles] = useState([]);

	const dispatch = useDispatch();

	useEffect(() => {
		if (allFilesAndImages?.length > 0) {
			const images = allFilesAndImages.filter((doc) => doc.docType.startsWith('ASSETS_IMAGE'));
			const files = allFilesAndImages.filter((doc) => doc.docType === 'ASSETS_FILE');

			setMultipleImageData([...images]);
			setUploadedFiles([...files]);
		}
	}, [allFilesAndImages]);

	useEffect(() => {
		if (onMultipleImageDataChange) {
			onMultipleImageDataChange(multipleImageData);
		}
	}, [multipleImageData, onMultipleImageDataChange]);

	const handleFileUpload = async (uploadFunc, files, updateFunc) => {
		dispatch(openLoader());
		try {
			const responseData = await uploadFunc(files);
			if (responseData) {
				updateFunc(responseData);
				dispatch(closeLoader());
			}
		} catch {
			dispatch(closeLoader());
		}
	};

	const updateDocumentIds = (images = multipleImageData, files = uploadedFiles) => {
		const imageIds = images.map((img) => img.id);
		const fileIds = files.map((file) => file.id);
		onImageIdsChange([...imageIds, ...fileIds]);
	};

	const onSingleImageUpload = async (imageInfo) => {
		await handleFileUpload(uploadSingleImage, imageInfo, (responseData) => {
			const tmpArray = multipleImageData?.filter((obj) => responseData.docType !== obj.docType);
			setMultipleImageData([...tmpArray, responseData]);
			updateDocumentIds([...tmpArray, responseData]);
		});
	};

	const multipleImageUpload = async (images) => {
		await handleFileUpload(uploadMultipleImage, images, (responseData) => {
			const docType = responseData.map((imageInfo) => imageInfo.docType);
			const tmpArray = multipleImageData?.filter((obj) => !docType.includes(obj.docType));
			setMultipleImageData([...tmpArray, ...responseData]);
			updateDocumentIds([...tmpArray, ...responseData]);
		});
	};

	const setDocumentTypeByFileName = (fileName, type) => {
		const name = fileName.toLowerCase();
		if (type === 'SKETCH') {
			if (name.endsWith('-f.jpg') || name.endsWith('-f.png')) return 'ASSETS_IMAGE_S1';
			if (name.endsWith('-b.jpg') || name.endsWith('-b.png')) return 'ASSETS_IMAGE_S2';
		} else if (type === 'ARTWORK') {
			if (name.endsWith('-s1.jpg') || name.endsWith('-s1.png')) return 'ASSETS_IMAGE_S1';
			if (name.endsWith('-s2.jpg') || name.endsWith('-s2.png')) return 'ASSETS_IMAGE_S2';
		}
		if (name.endsWith('-s3.jpg') || name.endsWith('-s3.png')) return 'ASSETS_IMAGE_S3';
		if (name.endsWith('-s4.jpg') || name.endsWith('-s4.png')) return 'ASSETS_IMAGE_S4';
		return null;
	};

	const processFiles = (files, type, validFileCallback) => {
		if (files.length > 4) {
			toastError('Please select only 4 images');
			return;
		}

		const invalidFiles = files.filter((item) => item.size > 1000000);
		if (invalidFiles.length > 0) {
			toastError('Each image size should be less than 1MB');
			return;
		}

		const validFileNames = files.every((file) => setDocumentTypeByFileName(file.name, type) !== null);
		if (!validFileNames) {
			toastError('Please upload valid name files.');
			return;
		}

		validFileCallback(files);
	};

	const onUploadMultipleImages = (e) => {
		e.preventDefault();
		const files = [...e.target.files];
		processFiles(files, type, (validFiles) => {
			const modifiedFileList = validFiles
				.map((file) => {
					if (isValidFile(file, 'image')) {
						const documentType = setDocumentTypeByFileName(file.name, type);
						const data = {
							name: file?.name,
							docMimeType: file?.type,
							documentType: documentType,
							base64Str: '',
							size: file?.size
						};
						const reader = new FileReader();
						reader.readAsDataURL(file);
						reader.onload = () => {
							data.base64Str = reader.result;
						};
						reader.onerror = (error) => toastError(error);
						return data;
					}
					return null;
				})
				.filter(Boolean);

			setTimeout(() => multipleImageUpload(modifiedFileList), 100);
		});
	};

	const handleRemoveFile = async (docId, removeFunc, updateFunc) => {
		dispatch(openLoader());
		try {
			const data = await removeSingleFile(docId, itemId);
			dispatch(closeLoader());
			if (data && data.success) {
				toastSuccess(data.message);
				updateFunc(docId);
			} else {
				updateFunc(docId);
			}
		} catch {
			dispatch(closeLoader());
		}
	};

	const removeImage = (docId) => handleRemoveFile(docId, removeSingleFile, imageRemoveFromArray);
	const imageRemoveFromArray = (docId) => {
		const tmpArray = multipleImageData?.filter((obj) => docId !== obj.id);
		setMultipleImageData(tmpArray);
		updateDocumentIds(tmpArray, uploadedFiles);
	};

	const onUploadFiles = async (e) => {
		const files = [...e.target.files];
		const allFileList = files.map((file) => {
			const data = {
				name: file?.name,
				docMimeType: file?.type === '' ? 'application/octet-stream' : file?.type,
				documentType: 'ASSETS_FILE',
				base64Str: ''
			};
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				data.base64Str = reader.result;
			};
			reader.onerror = (error) => toastError(error);
			return data;
		});

		setTimeout(
			() =>
				handleFileUpload(uploadMultipleImage, allFileList, (data) => {
					setUploadedFiles([...uploadedFiles, ...data]);
					toastSuccess('Successfully uploaded');
					updateDocumentIds(multipleImageData, [...uploadedFiles, ...data]);
				}),
			100
		);
	};

	const removeFile = (docId) => handleRemoveFile(docId, removeSingleFile, fileRemoveFromArray);
	const fileRemoveFromArray = (docId) => {
		const tmpArray = uploadedFiles?.filter((obj) => docId !== obj.id);
		setUploadedFiles(tmpArray);
		updateDocumentIds(multipleImageData, tmpArray);
	};

	const getSelectedTypeImage = (multipleImageData, documentType) => {
		return multipleImageData?.find((item) => item.docType === documentType);
	};

	const renderUploadedFiles = () => {
		return uploadedFiles?.map((item) => {
			return (
				<div className="single-upload" key={item.id}>
					<Tooltip title={item?.name} placement="top" arrow>
						<p
							className="regular-14 gray_dark_01 cursor-pointer"
							onClick={() => window.open(item?.docUrl, '_blank')}
						>
							{getShortName(item?.name, 12)}
						</p>
					</Tooltip>
					<div className="more-dropdown dropdown show">
						<button
							className="button text-only"
							type="button"
							id="moreInfo"
							data-toggle="dropdown"
							aria-expanded="true"
						>
							<img
								src="https://d2s0vx1fj6lpi0.cloudfront.net/icons/more_icon.svg"
								alt="more"
								className="mr-1"
							/>
						</button>
						<ul
							className="dropdown-menu dropdown-menu-right shadow-lg"
							role="menu"
							aria-labelledby="moreInfo"
							x-placement="bottom-end"
						>
							<li>
								<span onClick={() => window.open(item?.docUrl, '_blank')}>Download</span>
							</li>
							<li>
								<span className="red" onClick={() => removeFile(item?.id)}>
									Delete
								</span>
							</li>
						</ul>
					</div>
				</div>
			);
		});
	};

	return (
		<div>
			<div className="dashboard-container bg-white py-0">
				<div className="presentations-tab px-0 pt-0">
					<div className="presentations-upload-image-container">
						{/* add-img */}
						<UploadImage
							document={getSelectedTypeImage(multipleImageData, docType.ASSETS_IMAGE_S1)}
							title={type === 'SKETCH' ? 'Front-F' : 'Upload-S1'}
							docType="ASSETS_IMAGE_S1"
							singleImageUpload={onSingleImageUpload}
							removeImage={removeImage}
							documentGroup={null}
						/>
						<UploadImage
							document={getSelectedTypeImage(multipleImageData, docType.ASSETS_IMAGE_S2)}
							title={type === 'SKETCH' ? 'Back-B' : 'Upload-S2'}
							docType="ASSETS_IMAGE_S2"
							singleImageUpload={onSingleImageUpload}
							removeImage={removeImage}
							documentGroup={null}
						/>
						<UploadImage
							document={getSelectedTypeImage(multipleImageData, docType.ASSETS_IMAGE_S3)}
							title={type === 'SKETCH' ? 'Other-S3' : 'Upload-S3'}
							docType="ASSETS_IMAGE_S3"
							singleImageUpload={onSingleImageUpload}
							removeImage={removeImage}
							documentGroup={null}
						/>
						<UploadImage
							document={getSelectedTypeImage(multipleImageData, docType.ASSETS_IMAGE_S4)}
							title={type === 'SKETCH' ? 'Other-S4' : 'Upload-S4'}
							docType="ASSETS_IMAGE_S4"
							singleImageUpload={onSingleImageUpload}
							removeImage={removeImage}
							documentGroup={null}
						/>
						{error && <p className="error">{error}</p>}
					</div>

					<div className="upload-presentaion-and-psd add-pdf-files d-flex align-items-center p-3">
						<div className="all-presentaion mr-3">
							<div className="inline-upload">
								<label htmlFor="multipleUpload">
									<img src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`} />
									<span className="text-nowrap">All images</span>
								</label>
								<input
									type="file"
									className="file-upload"
									id="multipleUpload"
									name="image"
									onClick={(e) => (e.target.value = null)}
									onChange={(e) => onUploadMultipleImages(e)}
									accept="image/*"
									multiple={true}
								/>
							</div>
						</div>
						<div className="upload-psd-btn">
							<div className="inline-upload">
								<label htmlFor="po-upload" className="drag-upload">
									<img
										src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`}
										alt="Upload po"
									/>
									<span className="gray_dark_01 regular-14 text-nowrap">Upload file</span>
								</label>
								<input
									type="file"
									className="file-upload d-none"
									name
									id="po-upload"
									multiple
									accept=".pdf, .xls, .xlsx, .doc, .docx, .psd, .ai, .dxf, .zip, .eps"
									onClick={(e) => (e.target.value = null)}
									onChange={(e) => onUploadFiles(e)}
								/>
							</div>
						</div>
						<div className="all-uploaded-psd gap-2 d-flex align-items-center flex-wrap">
							{renderUploadedFiles()}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UploadFiles;
